import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useCreditBalance } from '../hooks/useCreditBalance';
import { useCreditHistory } from '../hooks/useCreditHistory';
import { useSubscription } from '../hooks/useSubscription';
import { supabase } from '../lib/supabase';
import { trackEvent } from '../lib/analytics';
import { REEDLE_TIERS } from '@papper/shared/reedle/tiers';
import { showErrorAlert, ErrorCategory } from '../lib/errorHandler';
import { hideStripePaymentUI, platform } from '../lib/native';
import { findPackage, purchasePackage, findCreditPackage, openSubscriptionManagement, restorePurchases } from '../lib/revenuecat';
import { fetchSubscription } from '../store/billing$';
import { CREDIT_PACK_SMALL, CREDIT_PACK_LARGE } from '../constants/pricing';
import UsageBalanceTab from './UsageBalanceTab';
import UsageHistoryTab from './UsageHistoryTab';
import UsagePlanTab from './UsagePlanTab';
import UsageDowngradeModal from './UsageDowngradeModal';
import UsageUpgradeModal from './UsageUpgradeModal';
import UsageUpgradeSuccessModal from './UsageUpgradeSuccessModal';
import UsageRestrictionModal from './UsageRestrictionModal';
import './UsageModal.css';

import { apiConfig } from '../lib/apiConfig';
import { Dialog, Tabs } from '../design-system';
const REEDLE_SERVER_URL = apiConfig.renderJsUrl;

const CURRENCY_CONFIG = {
  usd: {
    symbol: '$',
    label: 'USD',
    prices: { [CREDIT_PACK_SMALL]: '1.00', [CREDIT_PACK_LARGE]: '5.00' },
    alipay: false,
    paymentMethodKey: 'cardOnly'
  },
  gbp: {
    symbol: '£',
    label: 'GBP',
    prices: { [CREDIT_PACK_SMALL]: '0.80', [CREDIT_PACK_LARGE]: '4.00' },
    alipay: true,
    paymentMethodKey: 'cardAlipay'
  },
  cny: {
    symbol: '¥',
    label: 'CNY',
    prices: { [CREDIT_PACK_SMALL]: '7.00', [CREDIT_PACK_LARGE]: '35.00' },
    alipay: true,
    paymentMethodKey: 'cardAlipay'
  },
};

// Display feature keys for plan cards (i18n keys: usage.plan.features.*)
const PLAN_DISPLAY_FEATURES = {
  free: ['basicAi'],
  pro: ['audioTts', 'mcpApi'],
  max: ['priority', 'unlimited'],
};

// Derive plan card data from shared tier config
const PLAN_TIERS = Object.fromEntries(
  Object.entries(REEDLE_TIERS).map(([key, tier]) => [key, {
    name: tier.displayName,
    monthlyCredits: tier.monthlyCredits,
    priceMonthly: tier.priceMonthlyUsd,
    priceAnnual: tier.priceAnnualUsd,
    features: PLAN_DISPLAY_FEATURES[key] || [],
  }])
);

const UsageModal = ({ isOpen, onClose, activeTab = 'balance', onTabChange, presentation = 'dialog' }) => {
  const { t } = useTranslation(['modals', 'common']);
  const setActiveTab = onTabChange || (() => {});
  const [selectedCurrency, setSelectedCurrency] = useState('gbp'); // Default to GBP for Alipay
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' | 'annual'
  const { balance, tierCredits, permanentCredits, loading: balanceLoading, error: balanceError, refresh: refreshBalance } = useCreditBalance();
  const { logs, loading: historyLoading, hasMore, loadMore } = useCreditHistory();
  const { subscription, loading: subscriptionLoading, refresh: refreshSubscription } = useSubscription();
  const [topUpLoading, setTopUpLoading] = useState(null); // null or credits amount
  const [actionLoading, setActionLoading] = useState(null); // 'cancel' | 'resume' | 'portal' | 'subscribe:pro' | 'subscribe:max' | 'downgrade:pro' | 'downgrade:free' | null
  const [confirmModal, setConfirmModal] = useState(null); // { type: 'cancel' } | { type: 'downgrade', targetTier } | { type: 'upgrade', targetTier, proration, billingCycle } | { type: 'upgrade-success', targetTier } | null
  const [previewLoading, setPreviewLoading] = useState(null); // null or tier string ('pro' | 'max')
  const [restrictionModal, setRestrictionModal] = useState(null); // { reason: 'grace_period' }
  const [pendingTierActivation, setPendingTierActivation] = useState(null); // tier string while waiting for webhook after native purchase
  const [restoreLoading, setRestoreLoading] = useState(false);
  const currencyInfo = CURRENCY_CONFIG[selectedCurrency];
  const currentTier = subscription?.tier || 'free';
  const isOnTrial = subscription?.trial_end && new Date(subscription.trial_end) > new Date();
  const isInGracePeriod = subscription?.renewal_mode !== 'none' &&
    subscription?.current_period_end &&
    new Date(subscription.current_period_end) < new Date();
  const effectiveTier = isOnTrial ? 'free' : currentTier;

  // Analytics: track when pricing/usage modal is viewed — fire once per open cycle
  const pricingTrackedRef = useRef(false);
  useEffect(() => {
    if (isOpen && !pricingTrackedRef.current) {
      pricingTrackedRef.current = true;
      trackEvent('pricing_page_viewed', {
        current_plan: currentTier,
        balance: balance ?? null,
      });
    }
    if (!isOpen) pricingTrackedRef.current = false;
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-sync billing cycle to user's current subscription
  useEffect(() => {
    if (!subscriptionLoading && subscription) {
      const currentTierValue = subscription?.tier || 'free';
      const currentBillingCycle = subscription?.billing_cycle;

      // For paid users, lock billing cycle to their current subscription
      if (currentTierValue !== 'free' && currentBillingCycle) {
        setBillingCycle(currentBillingCycle);
      }
    }
  }, [subscription, subscriptionLoading]);

  // Clear pendingTierActivation once the tier change propagates via Realtime
  useEffect(() => {
    if (pendingTierActivation && effectiveTier === pendingTierActivation) {
      setPendingTierActivation(null);
    }
  }, [effectiveTier, pendingTierActivation]);

  // Clear pendingTierActivation when modal closes
  useEffect(() => {
    if (!isOpen) setPendingTierActivation(null);
  }, [isOpen]);

  // Determine if user can change billing cycle (only free users can)
  const canChangeBillingCycle = effectiveTier === 'free';

  // Handle billing cycle toggle click
  const handleBillingCycleClick = (cycle) => {
    if (!canChangeBillingCycle) {
      toast(t('usage.plan.billingCycleRestricted'), {
        description: t('usage.plan.billingCycleRestrictedDesc'),
        duration: 4000,
      });
      return;
    }
    setBillingCycle(cycle);
  };

  // Check if plan changes are restricted (grace period only)
  const showRestrictionWarning = () => {
    if (isInGracePeriod) {
      setRestrictionModal({ reason: 'grace_period' });
      return true;
    }
    return false;
  };

  // Handle restore purchases (native only — required by App Store guidelines)
  const handleRestorePurchases = async () => {
    setRestoreLoading(true);
    try {
      const result = await restorePurchases();
      // Staggered refresh to catch webhook processing at varying speeds
      setTimeout(() => refreshBalance(), 3000);
      setTimeout(() => refreshBalance(), 7000);
      setTimeout(() => refreshBalance(), 12000);
      setTimeout(() => fetchSubscription(), 3000);
      setTimeout(() => fetchSubscription(), 7000);
      setTimeout(() => fetchSubscription(), 12000);
      if (result?.hasActivePurchases) {
        toast.success(t('usage.restoreSuccess'));
      } else {
        toast(t('usage.restoreNothingFound'));
      }
    } catch {
      toast.error(t('usage.restoreError'));
    } finally {
      setRestoreLoading(false);
    }
  };

  // Handle top-up button click
  const handleTopUp = async (credits) => {
    setTopUpLoading(credits);

    // Track checkout initiated event
    trackEvent('checkout_initiated', {
      package_type: `${credits}_credits`,
      price: currencyInfo.prices[credits],
      currency: selectedCurrency,
      trigger_source: 'usage_modal',
    });

    try {
      // Get current session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        showErrorAlert(t('usage.errors.loginRequired'), null, { component: 'UsageModal', action: 'topup_no_session', category: ErrorCategory.AUTH });
        return;
      }

      const response = await fetch(`${REEDLE_SERVER_URL}/api/stripe/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ credits, currency: selectedCurrency, origin: window.location.origin }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('usage.errors.checkoutFailed'));
      }

      // Redirect to Stripe Checkout
      window.location.href = data.url;
    } catch (error) {
      console.error('Top-up error:', error);
      showErrorAlert(error.message || t('usage.errors.checkoutError'), error, { component: 'UsageModal', action: 'topup', category: ErrorCategory.PAYMENT });
    } finally {
      setTopUpLoading(null);
    }
  };

  // Handle native credit top-up via RevenueCat
  const handleNativeTopUp = async (credits) => {
    setTopUpLoading(credits);

    trackEvent('checkout_initiated', {
      package_type: `${credits}_credits`,
      platform: 'native',
      trigger_source: 'usage_modal',
    });

    try {
      const pkg = await findCreditPackage(credits);
      if (!pkg) {
        showErrorAlert(t('usage.errors.checkoutFailed'), null, {
          component: 'UsageModal',
          action: 'topup_no_package',
          category: ErrorCategory.PAYMENT,
        });
        return;
      }

      const result = await purchasePackage(pkg);
      if (result.cancelled) return;
      if (!result.success) {
        throw new Error(result.error || t('usage.errors.checkoutFailed'));
      }

      trackEvent('credits_purchased_native', { credits });
      toast.success(t('usage.creditTopUpSuccess', { credits: credits.toLocaleString() }));

      // Credits have no Realtime coverage — poll at staggered intervals to catch
      // webhook processing at varying speeds (cold start can take 5-10s)
      setTimeout(() => refreshBalance(), 3000);
      setTimeout(() => refreshBalance(), 7000);
      setTimeout(() => refreshBalance(), 12000);
    } catch (error) {
      console.error('Native top-up error:', error);
      showErrorAlert(error.message || t('usage.errors.checkoutError'), error, {
        component: 'UsageModal', action: 'topup_native', category: ErrorCategory.PAYMENT,
      });
    } finally {
      setTopUpLoading(null);
    }
  };

  // Fetch upgrade preview before showing confirmation modal
  const fetchUpgradePreview = async (tier) => {
    setPreviewLoading(tier);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        showErrorAlert(t('usage.errors.loginRequired'), null, { component: 'UsageModal', action: 'preview_no_session', category: ErrorCategory.AUTH });
        return;
      }

      const response = await fetch(`${REEDLE_SERVER_URL}/api/subscription/preview-upgrade`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ tier, billingCycle }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('usage.errors.checkoutFailed'));
      }

      // Show confirmation modal with proration data
      setConfirmModal({
        type: 'upgrade',
        targetTier: tier,
        proration: data.proration,
        billingCycle: billingCycle,
      });
    } catch (error) {
      console.error('Preview upgrade error:', error);
      showErrorAlert(error.message || t('usage.errors.checkoutError'), error, { component: 'UsageModal', action: 'preview_upgrade', category: ErrorCategory.PAYMENT });
    } finally {
      setPreviewLoading(null);
    }
  };

  // Handle confirm upgrade after user reviews proration
  const handleConfirmUpgrade = async () => {
    const { targetTier, billingCycle } = confirmModal;
    setActionLoading(`subscribe:${targetTier}`);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        showErrorAlert(t('usage.errors.loginRequired'), null, { component: 'UsageModal', action: 'confirm_upgrade_no_session', category: ErrorCategory.AUTH });
        return;
      }

      const response = await fetch(`${REEDLE_SERVER_URL}/api/subscription/upgrade`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ tier: targetTier, billingCycle }),
      });

      const data = await response.json();

      if (!response.ok) {
        // For payment errors, fallback to Stripe Checkout
        if (response.status === 402 && data.requiresCheckout) {
          console.log('[UsageModal] Payment failed in synchronous upgrade, falling back to Stripe Checkout');

          // Try checkout flow instead
          const checkoutResponse = await fetch(`${REEDLE_SERVER_URL}/api/stripe/create-subscription-checkout`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({ tier: targetTier, billingCycle, origin: window.location.origin }),
          });

          const checkoutData = await checkoutResponse.json();

          if (!checkoutResponse.ok) {
            let errorMessage = checkoutData.error || t('usage.errors.checkoutFailed');
            if (checkoutData.details) {
              errorMessage += `: ${checkoutData.details}`;
            }
            throw new Error(errorMessage);
          }

          // Redirect to Stripe Checkout to let user enter new payment method
          window.location.href = checkoutData.url;
          return;
        }

        // For other errors, show detailed error message
        let errorMessage = data.error || t('usage.errors.upgradeFailed');
        if (data.details) {
          errorMessage = `${errorMessage}: ${data.details}`;
        }

        const error = new Error(errorMessage);
        error.status = response.status;
        error.code = data.code;
        throw error;
      }

      // Show success modal
      setConfirmModal({
        type: 'upgrade-success',
        targetTier,
      });

      // Refresh subscription data to show new tier
      await refreshSubscription();
    } catch (error) {
      console.error('Confirm upgrade error:', error);
      showErrorAlert(error.message || t('usage.errors.checkoutError'), error, { component: 'UsageModal', action: 'confirm_upgrade', category: ErrorCategory.PAYMENT });
      setConfirmModal(null);
    } finally {
      setActionLoading(null);
    }
  };

  // Handle subscription checkout (upgrade to Pro/Max)
  const handlePlanAction = async (tier) => {
    if (tier === effectiveTier) return; // Already current plan

    const tierOrder = { free: 0, pro: 1, max: 2 };
    const isDowngrade = tierOrder[tier] < tierOrder[effectiveTier];

    // Native: handle all cases first — Stripe modals are hidden on native
    if (platform.isNative) {
      // Grace period: payment pending, can't change plan — show toast only
      if (isInGracePeriod) {
        toast(t('usage.plan.restrictionGracePeriodMessage'));
        return;
      }

      // Downgrade: can't do via IAP — direct to App Store subscription management
      if (isDowngrade) {
        toast(t('usage.plan.managedByStore', { store: 'App Store' }));
        try {
          await openSubscriptionManagement();
        } catch {
          toast.error(t('usage.errors.openStoreFailed', { defaultValue: 'Unable to open subscription management. Please manage via Settings > Apple ID > Subscriptions.' }));
        }
        return;
      }

      // Upgrade: use RevenueCat IAP
      setActionLoading(`subscribe:${tier}`);
      try {
        const pkg = await findPackage(tier, billingCycle);
        if (!pkg) {
          showErrorAlert(t('usage.errors.checkoutFailed'), null, { component: 'UsageModal', action: 'plan_action_no_package', category: ErrorCategory.PAYMENT });
          return;
        }

        const result = await purchasePackage(pkg);
        if (result.cancelled) return; // User dismissed payment sheet
        if (!result.success) {
          throw new Error(result.error || t('usage.errors.checkoutFailed'));
        }

        // Purchase succeeded — RC webhook will update DB → Realtime fires → billing$ updates.
        // Do NOT call refreshSubscription() immediately: it fetches stale pre-webhook data.
        // Set pendingTierActivation to block re-taps until tier change is confirmed.
        // Fallback: clear after 11s if Realtime never fires.
        trackEvent('subscription_purchased_native', { tier, billingCycle });
        toast.success(t('usage.plan.upgradeSuccessTitle'));
        setPendingTierActivation(tier);
        setTimeout(() => refreshSubscription(), 10000);
        setTimeout(() => setPendingTierActivation(null), 11000);
      } catch (error) {
        console.error('Native subscription error:', error);
        showErrorAlert(error.message || t('usage.errors.checkoutError'), error, { component: 'UsageModal', action: 'plan_action_native', category: ErrorCategory.PAYMENT });
      } finally {
        setActionLoading(null);
      }
      return;
    }

    // Web: use Stripe — check grace period and downgrade before proceeding

    // Check for grace period restriction (trial users can subscribe)
    if (isInGracePeriod) {
      showRestrictionWarning();
      return;
    }

    // For downgrades, show confirmation modal
    if (isDowngrade) {
      setConfirmModal({ type: 'downgrade', targetTier: tier });
      return;
    }

    // Web: use Stripe
    // Check if this is an upgrade (existing paid subscription) or new subscription
    const isUpgrade = effectiveTier !== 'free' && subscription?.stripe_subscription_id;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        showErrorAlert(t('usage.errors.loginRequired'), null, { component: 'UsageModal', action: 'plan_action_no_session', category: ErrorCategory.AUTH });
        return;
      }

      if (isUpgrade) {
        // For upgrades, fetch preview first and show confirmation modal
        await fetchUpgradePreview(tier);
        return;
      } else {
        // For new subscriptions (from free tier), use checkout flow
        setActionLoading(`subscribe:${tier}`);
        const response = await fetch(`${REEDLE_SERVER_URL}/api/stripe/create-subscription-checkout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ tier, billingCycle, origin: window.location.origin }),
        });

        const data = await response.json();

        if (!response.ok) {
          // Build detailed error message
          let errorMessage = data.error || t('usage.errors.checkoutFailed');

          // Include details if available
          if (data.details) {
            errorMessage += `: ${data.details}`;
          }

          const error = new Error(errorMessage);
          error.status = response.status;
          error.code = data.code;
          throw error;
        }

        // Redirect to Stripe Checkout
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Subscription action error:', error);
      showErrorAlert(error.message || t('usage.errors.checkoutError'), error, { component: 'UsageModal', action: 'plan_action', category: ErrorCategory.PAYMENT });
    } finally {
      if (!isUpgrade) {
        setActionLoading(null);
      }
    }
  };

  // Handle schedule downgrade
  const handleScheduleDowngrade = async (targetTier) => {
    setActionLoading(`downgrade:${targetTier}`);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        showErrorAlert(t('usage.errors.loginRequired'), null, { component: 'UsageModal', action: 'downgrade_no_session', category: ErrorCategory.AUTH });
        return;
      }

      const response = await fetch(`${REEDLE_SERVER_URL}/api/subscription/schedule-downgrade`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ targetTier }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('usage.errors.subscriptionFailed'));
      }

      // Refresh subscription data
      await refreshSubscription();
      setConfirmModal(null);
    } catch (error) {
      console.error('Schedule downgrade error:', error);
      showErrorAlert(error.message || t('usage.errors.subscriptionFailed'), error, { component: 'UsageModal', action: 'schedule_downgrade', category: ErrorCategory.PAYMENT });
    } finally {
      setActionLoading(null);
    }
  };

  // Handle cancel downgrade
  const handleCancelDowngrade = async () => {
    // Check for trial/grace period restriction
    if (showRestrictionWarning()) return;

    setActionLoading('cancel-downgrade');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        showErrorAlert(t('usage.errors.loginRequired'), null, {
          component: 'UsageModal',
          action: 'cancel_downgrade_no_session',
          category: ErrorCategory.AUTH,
        });
        return;
      }

      const response = await fetch(`${REEDLE_SERVER_URL}/api/subscription/cancel-downgrade`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('usage.errors.subscriptionFailed'));
      }

      // Show success toast
      toast.success(t('usage.plan.downgradeCancelSuccess'));

      // Refresh subscription data
      await refreshSubscription();
    } catch (error) {
      console.error('Cancel downgrade error:', error);
      showErrorAlert(
        error.message || t('usage.errors.subscriptionFailed'),
        error,
        { component: 'UsageModal', action: 'cancel_downgrade', category: ErrorCategory.PAYMENT }
      );
    } finally {
      setActionLoading(null);
    }
  };

  // Handle open billing management (Stripe portal on web, store subscriptions on native)
  const handleManageBilling = async () => {
    if (platform.isNative) {
      setActionLoading('portal');
      try {
        await openSubscriptionManagement();
      } catch {
        toast.error(t('usage.errors.openStoreFailed', { defaultValue: 'Unable to open subscription management. Please manage via Settings > Apple ID > Subscriptions.' }));
      } finally {
        setActionLoading(null);
      }
      return;
    }

    // Store-managed subscription viewed on web — can't open Stripe portal
    if (!subscription?.stripe_subscription_id && subscription?.renewal_mode === 'provider_managed') {
      const store = subscription?.last_payment_provider === 'google' ? 'Google Play' : 'App Store';
      alert(t('usage.plan.managedByStore', { store }));
      return;
    }

    setActionLoading('portal');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        showErrorAlert(t('usage.errors.loginRequired'), null, { component: 'UsageModal', action: 'billing_no_session', category: ErrorCategory.AUTH });
        return;
      }

      const response = await fetch(`${REEDLE_SERVER_URL}/api/stripe/create-portal-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ origin: window.location.origin }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('usage.errors.subscriptionFailed'));
      }

      // Open Stripe portal in new tab
      window.open(data.url, '_blank');
    } catch (error) {
      console.error('Manage billing error:', error);
      showErrorAlert(error.message || t('usage.errors.subscriptionFailed'), error, { component: 'UsageModal', action: 'manage_billing', category: ErrorCategory.PAYMENT });
    } finally {
      setActionLoading(null);
    }
  };

  if (!isOpen) return null;
  const isPage = presentation === 'page';
  const Surface = isPage ? 'div' : Dialog;

  // Calculate USD value (1 credit = $0.001)
  // const usdValue = balance ? (balance / 1000).toFixed(2) : '1.00';

  return (
    <Surface {...(isPage ? { className: 'usage-page-content' } : {
      isOpen, onClose, title: t('usage.creditUsage'), size: 'lg',
      closeLabel: t('common:button.close'),
      overlayClassName: 'usage-modal-overlay', className: 'usage-modal',
    })}>
        <Tabs
          ariaLabel={t('usage.creditUsage')}
          value={activeTab === 'credits' ? 'balance' : activeTab}
          onValueChange={setActiveTab}
          className={isPage ? 'usage-page-tabs' : 'usage-tabs'}
          items={['balance', 'history', 'plan'].map(value => ({ value, label: t(`usage.tabs.${value}`) }))}
        />

        {/* Tab Content */}
        {/* 'credits' is a semantic alias for 'balance' — same UI, different intent (mid-action block vs. manual open). See MONETIZE.md §3. */}
        {(activeTab === 'balance' || activeTab === 'credits') ? (
          <UsageBalanceTab
            balanceLoading={balanceLoading}
            balanceError={balanceError}
            balance={balance}
            refreshBalance={refreshBalance}
            tierCredits={tierCredits}
            permanentCredits={permanentCredits}
            effectiveTier={effectiveTier}
            selectedCurrency={selectedCurrency}
            setSelectedCurrency={setSelectedCurrency}
            currencyInfo={currencyInfo}
            currencyConfig={CURRENCY_CONFIG}
            topUpLoading={topUpLoading}
            handleTopUp={handleTopUp}
            handleNativeTopUp={handleNativeTopUp}
            planTiersPro={PLAN_TIERS['pro']}
            planTiersMax={PLAN_TIERS['max']}
            onSwitchToPlan={() => setActiveTab('plan')}
            handleRestorePurchases={handleRestorePurchases}
            restoreLoading={restoreLoading}
          />
        ) : activeTab === 'history' ? (
          <UsageHistoryTab
            historyLoading={historyLoading}
            logs={logs}
            hasMore={hasMore}
            loadMore={loadMore}
          />
        ) : (
          /* Plan Tab */
          <UsagePlanTab
            subscriptionLoading={subscriptionLoading}
            subscription={subscription}
            currentTier={currentTier}
            isOnTrial={isOnTrial}
            effectiveTier={effectiveTier}
            actionLoading={actionLoading}
            previewLoading={previewLoading}
            billingCycle={billingCycle}
            canChangeBillingCycle={canChangeBillingCycle}
            handleBillingCycleClick={handleBillingCycleClick}
            handlePlanAction={handlePlanAction}
            handleManageBilling={handleManageBilling}
            handleCancelDowngrade={handleCancelDowngrade}
            planTiers={PLAN_TIERS}
            onSwitchToBalance={() => setActiveTab('balance')}
            pendingTierActivation={pendingTierActivation}
            handleRestorePurchases={handleRestorePurchases}
            restoreLoading={restoreLoading}
          />
        )}


        {/* Downgrade Confirm Modal */}
        {confirmModal?.type === 'downgrade' && !hideStripePaymentUI && (
          <UsageDowngradeModal
            onClose={() => setConfirmModal(null)}
            confirmModal={confirmModal}
            currentTier={currentTier}
            subscription={subscription}
            handleScheduleDowngrade={handleScheduleDowngrade}
            actionLoading={actionLoading}
          />
        )}

        {/* Upgrade Confirm Modal */}
        {confirmModal?.type === 'upgrade' && !hideStripePaymentUI && (
          <UsageUpgradeModal
            onClose={() => setConfirmModal(null)}
            confirmModal={confirmModal}
            currentTier={currentTier}
            handleConfirmUpgrade={handleConfirmUpgrade}
            actionLoading={actionLoading}
          />
        )}

        {/* Upgrade Success Modal */}
        {confirmModal?.type === 'upgrade-success' && !hideStripePaymentUI && (
          <UsageUpgradeSuccessModal
            onClose={() => setConfirmModal(null)}
            confirmModal={confirmModal}
          />
        )}

        {/* Plan Change Restriction Modal (grace period only) */}
        {restrictionModal && !hideStripePaymentUI && (
        <UsageRestrictionModal
          onClose={() => setRestrictionModal(null)}
        />
      )}
    </Surface>
  );
};

export default UsageModal;
